import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import { BrandEnum } from '../_common/enum/brand.enum';
import {
    getBrandFontFamily, getBrandHighlightColor,
    getBrandPrimaryColor,
    getBrandPrimaryLightColor,
    getBrandSecondaryColor,
    getBrandVibrantColor
} from '../_helpers/brand.helper';

export const getThemeForBrand = (brand: BrandEnum) => {
    const base = createTheme(); // for breakpoints reuse

    const brandPrimary = getBrandPrimaryColor(brand);
    const brandSecondary = getBrandSecondaryColor(brand);
    const brandVibrant = getBrandVibrantColor(brand);
    const brandLight = getBrandPrimaryLightColor(brand);
    const brandHighlight = getBrandHighlightColor(brand);
    const brandFontFamily = getBrandFontFamily(brand);

    return createTheme({
        typography: {
            fontFamily: brandFontFamily,
            h1: {
                fontSize: '2.25rem', // 36px - for phone screens
                [base.breakpoints.up('sm')]: { fontSize: '3.5rem' },
                [base.breakpoints.up('md')]: { fontSize: '4rem' },
            },
            h2: {
                fontSize: '1.875rem', // 30px
                [base.breakpoints.up('sm')]: { fontSize: '3rem' },
                [base.breakpoints.up('md')]: { fontSize: '3.5rem' },
            },
            h3: {
                fontSize: '1.5rem', // 24px
                [base.breakpoints.up('sm')]: { fontSize: '2.25rem' },
                [base.breakpoints.up('md')]: { fontSize: '2.5rem' },
            },
            h4: {
                fontSize: '1.25rem', // 20px
                [base.breakpoints.up('sm')]: { fontSize: '2rem' },
            },
            h5: {
                fontSize: '1rem', // 16px
                [base.breakpoints.up('sm')]: { fontSize: '20px' },
                [base.breakpoints.up('md')]: { fontSize: '26px' },
            },
            h6: {
                fontSize: '0.875rem', // 14px
                [base.breakpoints.up('sm')]: { fontSize: '1.5rem' },
            },
            body1: {
                fontSize: '0.875rem', // 14px
                [base.breakpoints.up('sm')]: { fontSize: '1.125rem' },
            },
            body2: {
                fontSize: '0.75rem', // 12px
                [base.breakpoints.up('sm')]: { fontSize: '0.8rem' },
            },
            subtitle1: {
                fontSize: '1rem', // 16px 
                [base.breakpoints.up('sm')]: { fontSize: '1.3rem' },
            },
            subtitle2: {
                fontSize: '0.875rem', // 14px
                [base.breakpoints.up('sm')]: { fontSize: '1rem' },
            },

        },

        palette: {
            primary: {
                main: brandPrimary,
                light: lighten(brandPrimary, 0.2),
                dark: darken(brandPrimary, 0.2),
                contrastText: '#fff',
            },
            secondary: {
                main: brandSecondary,
                dark: darken(brandSecondary, 0.2),
                light: lighten(brandSecondary, 0.2),
                contrastText: '#fff',
            },
            pulse: {
                main: brandVibrant,
            },
            light: {
                main: brandLight
            },
            highlight: {
                main: brandHighlight
            }
        },

        components: {
            MuiAlert: {
                styleOverrides: {
                    root: {
                        fontSize: '1.2rem',
                        '@media (max-width:600px)': {
                            fontSize: '0.9rem',
                        },
                    },
                    message: {
                        fontSize: 'inherit',
                    },
                },
            },


            MuiAutocomplete: {
                styleOverrides: {
                    paper: { fontSize: '1rem', backgroundColor: '#e0e0e0' },
                    option: { fontSize: '1rem' },
                },
            },

            MuiCard: {
                styleOverrides: {
                    root: {
                        padding: '8px',
                        backgroundColor: '#f6f6f6',
                        borderRadius: '4px',
                        [base.breakpoints.up('sm')]: { padding: '16px' },
                    },
                },
            },

            MuiCardContent: {
                styleOverrides: {
                    root: { padding: '0px !important' },
                },
            },

            MuiMenu: {
                styleOverrides: {
                    paper: { maxHeight: 200, overflowY: 'auto', backgroundColor: '#e0e0e0' },
                },
            },

            MuiMenuItem: {
                styleOverrides: {
                    root: { fontSize: '1rem' },
                },
            },

            MuiPaper: {
                styleOverrides: {
                    root: { boxShadow: 'none' },
                },
            },

            MuiTableCell: {
                styleOverrides: {
                    root: { fontSize: '1rem !important' },
                },
            },

            MuiTableHead: {
                styleOverrides: {
                    root: {
                        '& .MuiTableCell-root': { fontSize: '0.9rem' },
                    },
                },
            },

            MuiToggleButton: {
                styleOverrides: {
                    root: {
                        fontSize: '1.2rem',
                        '@media (max-width:600px)': {
                            fontSize: '0.9rem',
                        },
                        '&.Mui-selected': {
                            backgroundColor: brandSecondary,
                            color: '#fff',
                            '&:hover': { backgroundColor: brandSecondary },
                        },
                    },
                },
            },


            MuiButton: {
                styleOverrides: {
                    root: {
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        width: '150px',
                        padding: 2,
                        margin: '0 auto',
                        [base.breakpoints.up('sm')]: {
                            fontSize: '1.1rem',
                            width: '200px',
                            padding: 1,
                        },
                        [base.breakpoints.up('md')]: {
                            fontSize: '1.2rem',
                            width: '200px',
                            padding: 2,
                        },
                        [base.breakpoints.up('lg')]: {
                            fontSize: '1.3rem',
                            width: '200px',
                            padding: 2,
                        },
                    },

                    containedPrimary: ({ theme }) => ({
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.getContrastText(theme.palette.primary.main),
                        '&:hover': {
                            backgroundColor: darken(theme.palette.primary.main, 0.12),
                            boxShadow: 'none',
                        },
                    }),

                    containedSecondary: ({ theme }) => ({
                        backgroundColor: theme.palette.secondary.main,
                        color: theme.palette.getContrastText(theme.palette.secondary.main),
                        '&:hover': {
                            backgroundColor: darken(theme.palette.secondary.main, 0.12),
                            boxShadow: 'none',
                        },
                    }),

                    outlinedPrimary: ({ theme }) => ({
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                        '&:hover': {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                    }),

                    outlinedSecondary: ({ theme }) => ({
                        borderColor: theme.palette.secondary.main,
                        color: theme.palette.secondary.main,
                        '&:hover': {
                            borderColor: theme.palette.secondary.dark,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.04),
                        },
                    }),
                },

                variants: [
                    {
                        props: { variant: 'soft', color: 'primary' },
                        style: ({ theme }) => {
                            const bg = alpha(theme.palette.primary.main, 0.18);
                            const hover = alpha(theme.palette.primary.main, 0.26);
                            return {
                                backgroundColor: bg,
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: hover },
                            };
                        },
                    },
                    {
                        props: { variant: 'soft', color: 'secondary' },
                        style: ({ theme }) => {
                            const bg = alpha(theme.palette.secondary.main, 0.18);
                            const hover = alpha(theme.palette.secondary.main, 0.26);
                            return {
                                backgroundColor: bg,
                                color: theme.palette.secondary.main,
                                '&:hover': { backgroundColor: hover },
                            };
                        },
                    },
                ],
            },

            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        marginBottom: '8px',
                        color: brandPrimary,
                        fontSize: '1.2rem',
                        '@media (max-width:600px)': {
                            fontSize: '0.9rem',
                        },
                    },
                    asterisk: {
                        color: 'red',
                    },
                },
            },

            MuiSlider: {
                styleOverrides: {
                    root: {
                        color: brandPrimary,
                        height: 6,
                        fontSize: '8px',
                        width: '100%',
                        paddingBottom: '10px !important',
                    },
                    thumb: { width: 16, height: 16, borderRadius: '4px', border: '1px solid #ccc' },
                    track: { border: 'none' },
                    rail: { opacity: 0.2 },
                    mark: { display: 'none' },
                    markLabel: { color: 'grey', fontSize: '0.70rem' },
                },
            },

            MuiInputBase: {
                styleOverrides: {
                    input: {
                        fontSize: '16px',
                        [base.breakpoints.up('sm')]: {
                            fontSize: '1.1rem',
                        },
                    },
                },
            },

            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        marginTop: '4px',
                        fontSize: '1.1rem',
                        color: brandPrimary,
                        '@media (max-width:600px)': {
                            fontSize: '0.8rem',
                        },
                        '&.Mui-error': {
                            color: '#d32f2f', // MUI default error red (optional)
                        },
                    },
                },
            },

            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        fontSize: '16px',
                        [base.breakpoints.up('sm')]: {
                            fontSize: '1rem',
                        },
                    },
                },
            },

            MuiButtonGroup: {
                styleOverrides: {
                    root: {
                        width: "100%",
                        display: "flex",
                        gap: "8px", 
                        marginBottom: "8px"
                    },
                    grouped: {
                        flex: 1,             
                        minWidth: 0,
                        borderRadius: 4,     
                        border: "1px solid",  
                        paddingInline: 16,        
                        overflow: "hidden",
                        "@media (max-width:600px)": {
                            fontSize: "0.55rem",
                            fontWeight: "bold",
                            paddingInline: 8,
                        },
                    },
                    groupedOutlined: {
                        borderColor: "currentColor",
                    },
                },
            }
        },
    });
};
